/**
 * useDeleteJob - Confirm-then-delete for a work order or inspection.
 *
 * Beta feedback (Oct 2026): "The Delete option is not working for old work
 * orders we want to delete to clean up the que." The list rows offered
 * **Delete**, but the menu item only logged to the console — nothing was
 * ever sent. This hook is the one real implementation, shared by the Work
 * Orders and Inspections lists and both detail pages:
 *
 *   - asks first (a ConfirmModal naming the job),
 *   - soft-deletes it through the API (recoverable from the database; the
 *     server keeps the row with deleted_at set),
 *   - refreshes every cached read the job appeared in (lists, dashboard,
 *     recurring series), and
 *   - shows the server's own reason if it refuses (e.g. a role that may not
 *     delete).
 *
 * Returns the trigger plus the modal element to render, the same shape as
 * useNotifyPrompt.
 *
 * @example
 * const { requestDelete, deleteModal } = useDeleteJob('work_order');
 * …
 * { label: 'Delete', variant: 'danger', onClick: () => requestDelete(wo.id, wo.projectName) }
 * …
 * {deleteModal}
 */
import React, { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmModal } from '../components/organisms/ConfirmModal';
import { Alert } from '../components/atoms';
import { workOrderService } from '../services/workOrderService';
import { inspectionService } from '../services/inspectionService';
import { invalidateJobCaches } from '../lib/invalidateJobCaches';
import { apiErrorMessage } from '../utils/apiErrorMessage';

export type DeletableJobEntity = 'work_order' | 'inspection';

export interface UseDeleteJobOptions {
	/** Runs after a successful delete — e.g. a detail page navigating away. */
	onDeleted?: () => void;
}

export interface UseDeleteJobResult {
	/** Open the confirmation for one job. `label` names it in the prompt. */
	requestDelete: (id: string | number, label?: string | null) => void;
	/** The confirmation modal — render it once anywhere in the page. */
	deleteModal: React.ReactNode;
}

const NOUN: Record<DeletableJobEntity, string> = {
	work_order: 'work order',
	inspection: 'inspection',
};

export function useDeleteJob(entity: DeletableJobEntity, options: UseDeleteJobOptions = {}): UseDeleteJobResult {
	const { onDeleted } = options;
	const queryClient = useQueryClient();
	const [target, setTarget] = useState<{ id: string | number; label: string | null } | null>(null);
	const [deleting, setDeleting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const requestDelete = useCallback((id: string | number, label?: string | null) => {
		setError(null);
		setTarget({ id, label: label ?? null });
	}, []);

	const close = useCallback(() => {
		if (deleting) return;
		setTarget(null);
		setError(null);
	}, [deleting]);

	const confirm = useCallback(async () => {
		if (!target) return;
		setDeleting(true);
		setError(null);
		try {
			const service = entity === 'work_order' ? workOrderService : inspectionService;
			await service.delete(String(target.id));
			/* A recurring occurrence disappears from its series page too. */
			invalidateJobCaches(queryClient, entity, { includeRecurrence: true });
			setTarget(null);
			onDeleted?.();
		} catch (err) {
			setError(apiErrorMessage(err, `Could not delete this ${NOUN[entity]}. Please try again.`));
		} finally {
			setDeleting(false);
		}
	}, [target, entity, queryClient, onDeleted]);

	const noun = NOUN[entity];
	const deleteModal = (
		<ConfirmModal
			isOpen={target !== null}
			onClose={close}
			onConfirm={confirm}
			title={`Delete ${noun}`}
			confirmLabel="Delete"
			variant="danger"
			loading={deleting}
			dataRef="hook-use-delete-job"
		>
			<p className="text-14px font-sans text-brand-text">
				{target?.label
					? <>Delete <span className="font-semibold">{target.label}</span>? It will be removed from every list.</>
					: `Delete this ${noun}? It will be removed from every list.`}
			</p>
			{error && <Alert variant="error" size="sm" icon className="mt-12px">{error}</Alert>}
		</ConfirmModal>
	);

	return { requestDelete, deleteModal };
}

export default useDeleteJob;
